import {
  ButtonWrapper,
  FieldWrapper,
  errorStyles,
  formContainerStyles,
  serverErrorStyles,
  timeSlotHintStyles,
} from './styles/SectionForm.styles';
import { Controller, useForm } from 'react-hook-form';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import Button from '@vfuk/core-button';
import Checkbox from '../../../Checkbox';
import { ReCaptchaInstance } from 'recaptcha-v3';
import RichTextElement from '../../Elements/RichText/RichText';
import SelectInputWithLabel from '@vfuk/core-select-input-with-label';
import SimpleGrid from '@vfuk/core-simple-grid';
import TextAreaInputWithLabel from '@vfuk/core-text-area-input-with-label';
import TextInputWithLabel from '@vfuk/core-text-input-with-label';
import TimeSlotPicker from './TimeSlotPicker';
import _ from 'lodash';
import addServerErrors from '../../../../helpers/addServerErrors';
import { loadRecaptcha } from '../../../../helpers/recaptchaHelpers';
import { navigate } from 'gatsby';
import runSeoEventData from '../../../../helpers/runSeoData';
import { submitContactForm } from '../../../../services/api';
import { DirectionContext } from '../../../../contexts/DirectionContext';

// import { DevTool } from '@hookform/devtools';

//@ts-ignore
interface ISectionFormProps {
  nodeLocale: string;
  token: string;
  entryId: string;
  slug: string;
  richText?: {
    text?: {
      raw: string;
    };
  };
  seoEventData?: string;
  datePickerTitle: string;
  datePickerPlaceholderText: string;
  datePickerDateFormat: string;
  timePickerTitle: string;
  timePickerPlaceholderText: string;
  timePickerCaption: string;
  optionalFieldIndicatorText: string;
  datePickerTimePickerSeparatorText: string;
  serverErrorMessage: string;
  formInputs: {
    __typename: string;
    name: string;
    buttons: any[];
    fieldType: string;
    id: string;
    label: string;
    rows?: number;
    hint?: {
      hint: string;
    };
    required: boolean;
    placeholder: string;
    options: string[];
    desktopWidth: string;
    tabletWidth: string;
  }[];
  operatingDays?:{
    __typename: string;
    id: string;
    title: string; 
    day: string;
    startTime: string;
    endTime: string;
  }[];
}

const SectionForm: React.FC<ISectionFormProps> = props => {
  const {
    nodeLocale,
    formInputs,
    operatingDays,
    token,
    entryId,
    slug,
    richText,
    seoEventData,
    datePickerTitle,
    datePickerPlaceholderText,
    datePickerDateFormat,
    timePickerTitle,
    timePickerPlaceholderText,
    timePickerCaption,
    optionalFieldIndicatorText,
    datePickerTimePickerSeparatorText,
    serverErrorMessage
  } = props;

  const [isLoading, setLoading] = useState(true);
  const [recaptcha, setRecaptcha] = useState<ReCaptchaInstance | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState(false);

  const direction = useContext(DirectionContext);

  const defaultServerErrorMessage =
    'Form submission unsuccessful, please try again later.';

  // CTA button is not a form type (it doesn't have a field type) so
  // for now we will make it a special case. NB - The submit button
  // will always go at the bottom irrespective fo where it appears in
  // formInputs.

  let useCtaButton = false;
  let buttonRowAlignment = 'centre';
  const hasRichText = richText !== undefined;
  let htmlAttributes = {};
  let customAttributeData;

  const { buttons } =
    formInputs.find(
      formInput =>
        formInput.__typename === 'ContentfulSectionElementCtaButtonRow'
    ) || {};

  if (buttons !== undefined && Array.isArray(buttons) && buttons.length > 0) {
    useCtaButton = true;
    buttonRowAlignment = buttons[0].buttonRowAlignment
      ? buttons[0].buttonRowAlignment
      : buttonRowAlignment;
    htmlAttributes = {
      type: 'submit',
    };
    customAttributeData = buttons[0].customAttributeData
      ? JSON.parse(buttons[0].customAttributeData.customAttributeData)
      : {};
  }

  const defaultValues = useMemo(() => {
    return formInputs
      .filter(
        input => input.__typename === 'ContentfulSectionElementInputField'
      )
      .reduce<Record<string, any>>((acc, cv) => {
        switch (cv.fieldType) {
          case 'checkbox': {
            acc[cv.name] = false;
            break;
          }

          default: {
            acc[cv.name] = '';
            break;
          }
        }

        return acc;
      }, {});
  }, []);

  // Form Validation Schema & Rules
  const { handleSubmit, control, formState, setError } = useForm({
    mode: 'onTouched',
    defaultValues,
  });

  const createOperatingTimes =()=> {
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    let operatingTimes = [];

    if (operatingDays && operatingDays.length > 0) {
      const operatingDaysMap = new Map(
        operatingDays.map(operatingDay => [operatingDay.day, operatingDay])
      );
      operatingTimes = days.map(day => {
        if (operatingDaysMap.has(day)) {
          return operatingDaysMap.get(day);
        } else {
          return {
            day: 'notOperatingDay',
            startTime: '00:00',
            endTime: '00:00',
          };
        }
      });
    } else {
      operatingTimes = days.map(day => {
        return { day: day, startTime: '07:00', endTime: '19:00' };
      });
    }
    return operatingTimes;
  }

  const operatingTimes = createOperatingTimes();

  const runContactFormEventData = (sData: Record<string, any>) => {
    if ((window as any).onContactFormUserEvent && sData) {
      try {
        (window as any).onContactFormUserEvent('contact_form', { ...sData });
      } catch (e) {
        console.log(
          'onContactFormUserEvent failed - please check the Filter SEO script JSON and that a SeoInteractionScript is correctly set up.'
        );
      }
    }
  };

  let touched = false;
  useEffect(() => {
    const inputFields = document.querySelectorAll('input');
    for (let i = 0; i < inputFields.length; i++) {
      inputFields[i].addEventListener('input', () => {
        const seoData = {
          event_type: 'touch',
        };
        if (!touched) {
          touched = true;
          runContactFormEventData(seoData);
        }
      });
    }
  }, []);

  const onSubmit = useCallback(
    async (formData, e) => {
      console.log('Submitting ...');
      console.log('Submited values: ', formData);
      e.preventDefault();
      setLoading(true);

      if (!recaptcha) {
        setLoading(false);
        setSubmitted(false);
        return;
      }

      try {
        const result = await submitContactForm({
          recaptchaInstance: recaptcha,
          data: {
            data: formData,
          },
          nodeLocale,
          entryId,
          formType: 'sectionForm',
        });

        if (result?.success) {
          const seoData = {
            event_type: 'submit',
          };
          runContactFormEventData(seoData);
          await navigate(`/${slug}`);
          setSubmitted(true);
        } else if (result?.errors) {
          if ('General' in result?.errors == false) {
            const seoData = {
              event_type: 'validation_error',
            };
            runContactFormEventData(seoData);
          }
          if ('ServerError' in result?.errors) {
            setServerError(true);
          } else {
            setServerError(false);
          }
          const seoData = {
            event_type: 'submission_error',
          };
          addServerErrors(result.errors, setError as any);
          runContactFormEventData(seoData);
        }
      } finally {
        setLoading(false);
        setSubmitted(false);
      }
    },
    [recaptcha, submitted]
  );

  useEffect(() => {
    loadRecaptcha(token)
      .then(
        recaptchaResult => {
          setRecaptcha(recaptchaResult);
        },
        error => {
          console.error(error);
        }
      )
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const getGridLayout = () => {
    const FULL_WIDTH = 12;
    const ONE_HALF_WIDTH = 6;
    const DEVICE_WIDTH = {
      undefined: FULL_WIDTH,
      fullWidth: FULL_WIDTH,
      shortFullWidth: FULL_WIDTH,
      oneHalfWidth: ONE_HALF_WIDTH,
    };
    const mobileLayout: any = [];
    const desktopLayout: any = [];

    formInputs.forEach(input => {
      if (
        input.__typename === 'ContentfulSectionElementRichText' ||
        input.__typename === 'ContentfulSectionElementCtaButtonRow'
      ) {
        mobileLayout.push(FULL_WIDTH);
        desktopLayout.push(FULL_WIDTH);
      } else {
        if ('desktopWidth' in input || 'tabletWidth' in input) {
          //@ts-ignore
          mobileLayout.push(DEVICE_WIDTH[input.tabletWidth]);
          //@ts-ignore
          desktopLayout.push(DEVICE_WIDTH[input.desktopWidth]);
        }
      }
    });

    return {
      sm: mobileLayout,
      md: desktopLayout,
      lg: desktopLayout,
      xl: desktopLayout,
    };
  };

  const gridLayout = getGridLayout();

  useEffect(() => {
    const element = document.querySelector('[class^="FieldWrapperstyle__OptionalOrRequiredLabel-sc"]');
    if (element) {
      element.innerHTML = optionalFieldIndicatorText;
    }
  }, []);

  return (
    <div css={formContainerStyles}>
      {/* Uncomment below to use the React Hook Form Dev Tool */}
      {/* <DevTool control={control} /> */}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {(formInputs.length || hasRichText) && (
          <SimpleGrid spacing={4} layout={gridLayout}>
            {formInputs.map(
              ({
                id,
                fieldType,
                name,
                label,
                rows,
                hint,
                required,
                placeholder,
                options,
                desktopWidth,
                tabletWidth,
                __typename,
              }) => {
                if (fieldType == 'text') {
                  return (
                    <Controller
                      key={id}
                      control={control}
                      name={name}
                      render={({
                        field: { onChange, onBlur, value, name },
                        fieldState: { error },
                      }) => {
                        return (
                          <FieldWrapper
                            desktopWidthIsShort={
                              desktopWidth === 'shortFullWidth'
                            }
                            tabletWidthIsShort={
                              tabletWidth === 'shortFullWidth'
                            }
                          >
                            <TextInputWithLabel
                              fieldWrapper={{
                                label: label,
                                optional: !required,
                                width: 'full',
                              }}
                              textInput={{
                                type: 'text',
                                id: id,
                                placeholder: placeholder,
                                value: value,
                                onChange: onChange,
                                onBlur: onBlur,
                                dataAttributes: { 'data-testid': name },
                                required: required,
                                name: name,
                              }}
                              state={error?.type && 'error'}
                            />
                            {error?.type === 'server' && (
                              <p css={errorStyles}>
                                {_.capitalize(error.message)}
                              </p>
                            )}
                            {required && error?.type === 'required' && (
                              <p css={errorStyles}>
                                {_.capitalize(error.message)}
                              </p>
                            )}
                            {error?.type === 'email' && (
                              <p css={errorStyles}>
                                {_.capitalize(error.message)}
                              </p>
                            )}
                            {error?.type === 'typeError' && (
                              <p css={errorStyles}>
                                Phone number is a required field & must be a
                                valid number
                              </p>
                            )}
                          </FieldWrapper>
                        );
                      }}
                    />
                  );
                } else if (fieldType == 'checkbox') {
                  return (
                    <Controller
                      key={id}
                      control={control}
                      name={name}
                      render={({
                        field: { onChange, onBlur, value, name },
                        fieldState: { error },
                      }) => {
                        return (
                          <>
                            <FieldWrapper
                              desktopWidthIsShort={
                                desktopWidth === 'shortFullWidth'
                              }
                              tabletWidthIsShort={
                                tabletWidth === 'shortFullWidth'
                              }
                            >
                              <Checkbox
                                //@ts-ignore
                                className="checkbox"
                                required={required}
                                name={name}
                                inputProps={{
                                  name,
                                  value,
                                  required,
                                }}
                                content={label}
                                //@ts-ignore
                                hint={hint?.hint}
                                value={value}
                                onBlur={onBlur}
                                onChange={onChange}
                                errorMessage={error?.message}
                                data-testid={`${name}`}
                              ></Checkbox>
                              {serverError && (
                                <p css={serverErrorStyles}>{_.capitalize(serverErrorMessage || defaultServerErrorMessage)}</p>
                              )}
                            </FieldWrapper>
                          </>
                        );
                      }}
                    />
                  );
                } else if (fieldType == 'text-area') {
                  return (
                    <Controller
                      key={id}
                      control={control}
                      name={name}
                      render={({
                        field: { onChange, onBlur, value, name },
                        fieldState: { error },
                      }) => {
                        return (
                          <div data-testid={`${name}`}>
                            <FieldWrapper
                              desktopWidthIsShort={
                                desktopWidth === 'shortFullWidth'
                              }
                              tabletWidthIsShort={
                                tabletWidth === 'shortFullWidth'
                              }
                            >
                              <TextAreaInputWithLabel
                                fieldWrapper={{
                                  label: label,
                                  optional: !required,
                                  width: 'full',
                                }}
                                textAreaInput={{
                                  id: id,
                                  value: value,
                                  onChange: onChange,
                                  onBlur: onBlur,
                                  required: required,
                                  name: name,
                                  placeholder: placeholder,
                                  rows: rows,
                                }}
                                state={error?.type && 'error'}
                              />
                            </FieldWrapper>
                            {error?.type === 'server' && (
                              <p css={errorStyles}>
                                {_.capitalize(error.message)}
                              </p>
                            )}
                            {required && error?.type === 'required' && (
                              <p css={errorStyles}>
                                {_.capitalize(error.message)}
                              </p>
                            )}
                          </div>
                        );
                      }}
                    />
                  );
                } else if (fieldType == 'select') {
                  {
                    const optionObject: any = [
                      { text: placeholder, value: undefined },
                    ];
                    options.forEach(option => {
                      optionObject.push({
                        text: option,
                        value: option,
                      });
                    });
                    return (
                      <Controller
                        key={id}
                        control={control}
                        name={name}
                        render={({
                          field: { onChange, onBlur, value, name },
                          fieldState: { error },
                        }) => {
                          return (
                            <div data-testid={`${name}`}>
                              <FieldWrapper
                                desktopWidthIsShort={
                                  desktopWidth === 'shortFullWidth'
                                }
                                tabletWidthIsShort={
                                  tabletWidth === 'shortFullWidth'
                                }
                              >
                                <SelectInputWithLabel
                                  fieldWrapper={{
                                    label: label,
                                    optional: !required,
                                    width: 'full',
                                  }}
                                  selectInput={{
                                    id: id,
                                    value: value ? value : undefined,
                                    onChange: onChange,
                                    onBlur: onBlur,
                                    required: required,
                                    name: name,
                                    options: optionObject,
                                  }}
                                  state={error?.type && 'error'}
                                />
                                {error?.type === 'server' && (
                                  <p css={errorStyles}>
                                    {_.capitalize(error.message)}
                                  </p>
                                )}
                                {required && error?.type === 'required' && (
                                  <p css={errorStyles}>
                                    {_.capitalize(error.message)}
                                  </p>
                                )}
                              </FieldWrapper>
                            </div>
                          );
                        }}
                      />
                    );
                  }
                } else if (fieldType == 'time-slot') {
                  return (
                    <Controller
                      key={id}
                      control={control}
                      name={name}
                      render={({
                        field: { onChange, value, name },
                        fieldState: { error },
                      }) => {
                        return (
                          <>
                            <FieldWrapper
                              desktopWidthIsShort={
                                desktopWidth === 'shortFullWidth'
                              }
                              tabletWidthIsShort={
                                tabletWidth === 'shortFullWidth'
                              }
                            >
                              {hint?.hint && (
                                <span css={timeSlotHintStyles}>
                                  {hint.hint}
                                </span>
                              )}
                              <TimeSlotPicker
                                name={name}
                                label={label}
                                datePickerTitle={datePickerTitle}
                                datePickerPlaceholderText={datePickerPlaceholderText}
                                datePickerDateFormat={datePickerDateFormat}
                                timePickerTitle={timePickerTitle}
                                timePickerPlaceholderText={timePickerPlaceholderText}
                                timePickerCaption={timePickerCaption}
                                optionalFieldIndicatorText={optionalFieldIndicatorText}
                                datePickerTimePickerSeparatorText={datePickerTimePickerSeparatorText}
                                value={value}
                                isRequired={required}
                                onChange={onChange}
                                operatingTimes={operatingTimes}
                                error={!!error}
                                direction={direction}
                              />
                              {error?.type === 'server' && (
                                <p css={errorStyles}>
                                  {_.capitalize(error.message)}
                                </p>
                              )}
                            </FieldWrapper>
                          </>
                        );
                      }}
                    />
                  );
                } else if (__typename == 'ContentfulSectionElementRichText') {
                  return (
                    <RichTextElement
                      key={id}
                      className="hint-message"
                      rawRichText={richText?.text?.raw}
                      color={richText.color}
                      textAlign={richText.textAlign}
                    />
                  );
                } else {
                  return null;
                }
              }
            )}

            <div className="ctaButton-wrapper">
              {useCtaButton && buttons && (
                <ButtonWrapper>
                  <SimpleGrid
                    columns={{
                      sm: 1,
                      md: 4,
                    }}
                    spacing={12}
                    justify={
                      buttonRowAlignment === 'centre'
                        ? 'center'
                        : buttonRowAlignment
                    }
                    verticalAlign="center"
                  >
                    <Button
                      {...customAttributeData}
                      htmlAttributes={{ ...htmlAttributes }}
                      id={buttons[0].id}
                      text={buttons[0].label}
                      appearance={
                        buttons[0].rank !== 'tertiary'
                          ? buttons[0].rank
                          : 'secondary'
                      }
                      inverse={buttons[0].rank === 'tertiary'}
                      title={buttons[0].label}
                      loading={isLoading}
                      onClick={() =>
                        runSeoEventData(buttons[0].seoEventData.seoEventData)
                      }
                    />
                  </SimpleGrid>
                </ButtonWrapper>
              )}
            </div>
          </SimpleGrid>
        )}
      </form>
    </div>
  );
};

export default SectionForm;
